import { durations } from '@jane/bloom/utils/durations';

const MAX_DISMISSALS = 3;
export const SNOOZE_MS = durations.ONE_WEEK * 1000;

export interface PromptDismissal {
  count: number;
  ids: number[];
  isSnoozed: boolean;
}

export const NO_DISMISSAL: PromptDismissal = {
  count: 0,
  ids: [],
  isSnoozed: false,
};

interface StoredDismissal {
  count: number;
  dismissedAt: number;
  ids: number[];
}

const parseStored = (value: string): StoredDismissal | null => {
  try {
    const parsed: unknown = JSON.parse(value);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const { count, dismissedAt, ids } = parsed as Partial<StoredDismissal>;
    if (
      typeof count !== 'number' ||
      typeof dismissedAt !== 'number' ||
      !Array.isArray(ids) ||
      !ids.every(Number.isInteger)
    )
      return null;
    return { count, dismissedAt, ids };
  } catch {
    return null;
  }
};

export const parseDismissal = (
  value: string | undefined,
  now: number
): PromptDismissal => {
  const stored = value ? parseStored(value) : null;
  if (!stored) return NO_DISMISSAL;
  return {
    count: stored.count,
    ids: stored.ids,
    isSnoozed: now < stored.dismissedAt + SNOOZE_MS,
  };
};

export const serializeDismissal = (
  { count, ids }: PromptDismissal,
  now: number
) => JSON.stringify({ count, dismissedAt: now, ids } satisfies StoredDismissal);

export const shouldShowPrompt = (
  dismissal: PromptDismissal,
  productIds: number[]
) =>
  productIds.length > 0 &&
  (productIds.some((id) => !dismissal.ids.includes(id)) ||
    (dismissal.count < MAX_DISMISSALS && !dismissal.isSnoozed));

export const nextDismissal = (
  previous: PromptDismissal,
  productIds: number[],
  countsAsDismissal: boolean
): PromptDismissal => {
  const isSameProducts = productIds.every((id) => previous.ids.includes(id));
  const count = isSameProducts ? previous.count : 0;
  return {
    count: countsAsDismissal ? count + 1 : count,
    ids: productIds,
    isSnoozed: true,
  };
};
